import { useEffect, useState } from 'react';

import { TextField, Grid } from '@mui/material';
import { useField } from 'formik';

import { useAppSelector } from 'src/utils/hooks';

import TooltipReusable from 'src/components/TooltipReusable';
import QuestionLayout from './QuestionLayout';

interface Props {
  question: Question;
}

export default function TextQuestion({ question }: Props) {
  const [orderIbanField] = useField('orderIban');
  const step = useAppSelector((state) => state?.stepper.steps);
  const { isColorBlindMode } = useAppSelector((state) => state.config);
  const [{ value, ...field }, meta, helper] = useField(
    question.key.replace(/\./g, '&').replace(/\[\]/g, '$')
  );

  const [isDisabled, setIsDisabled] = useState(question.readonly);

  useEffect(() => {
    if (step == 1 && question.key === 'orderBankName') {
      const isForeignIban = orderIbanField.value.substring(0, 2) !== 'DE';
      const isEmptyIban = orderIbanField.value.length === 0;

      if (isForeignIban) {
        setIsDisabled(isEmptyIban);
        if (!isEmptyIban) {
          helper.setTouched(false);
          helper.setValue('');
        }
      } else {
        setIsDisabled(true);
      }
    }
  }, [orderIbanField.value]);

  return (
    <QuestionLayout question={question} meta={meta}>
      <Grid item minWidth={'100%'}>
        <TextField
          error={meta.touched && Boolean(meta.error)}
          value={
            isDisabled
              ? question?.answer?.value || value
              : value || question?.answer?.value
          }
          {...field}
          hiddenLabel
          fullWidth
          disabled={isDisabled}
          variant="filled"
          sx={{
            backgroundColor: 'inputFields.bgColor',
            input: {
              color: 'inputFields.main',
            },
            '.MuiInputBase-root': {
              backgroundColor: 'inputFields.bgColor',
            },
            '.MuiSvgIcon-root': {
              color: 'inputFields.main',
            },
            label: {
              color: 'inputFields.main',
            },
            '& .Mui-disabled': {
              backgroundColor: isColorBlindMode
                ? 'transparent !important'
                : 'inputFields.bgColor !important',
            },

            '& .MuiFilledInput-root:before': {
              borderBottom: (theme) =>
                `1px solid ${theme.palette.inputFields?.main || '#000000'}`,
            },
            '& .MuiFilledInput-root:after': {
              borderBottom: (theme) =>
                `1px solid ${theme.palette.inputFields?.main || '#000000'}`,
            },

            ...(isDisabled && {
              '& .MuiFilledInput-root:before': {
                borderBottom: 'none !important',
              },
              '& .MuiFilledInput-root:after': {
                borderBottom: 'none !important',
              },
            }),
          }}
        />
      </Grid>
      <Grid item paddingLeft={'20px'}>
        {question.answer.prefilled && !question.readonly && (
          <TooltipReusable
            title={[
              'Diese Angabe wurde aus Ihren vorherigen Angaben übernommen. Bitte überprüfen Sie diese auf Korrektheit.',
            ]}
            iconFlag={false}
          />
        )}
      </Grid>
    </QuestionLayout>
  );
}
